import { useEffect, useState } from "react";
import ChartError from "../components/ChartError";
import ChartPlaceholder from "../components/ChartPlaceholder";

const CHART_HEIGHT = 360;

/** Stands in for an upstream that returns a long, unhelpful body. */
const LONG_BODY = `Query failed with HTTP 500. ${"upstream connect error or disconnect/reset before headers. reset reason: connection termination; ".repeat(6)}`;

interface Scenario {
  id: string;
  /** What this card is demonstrating. */
  label: string;
  title?: string;
  detail?: string;
  retryable: boolean;
}

const SCENARIOS: Scenario[] = [
  {
    id: "unreachable",
    label: "Endpoint not routed — what a static build with no proxy returns",
    title: "Rate-limiting data unavailable",
    detail:
      "Query failed with HTTP 404. The endpoint returned a web page instead of JSON, so Prometheus is most likely not reachable from this environment.",
    retryable: true,
  },
  {
    id: "timeout",
    label: "Reachable but silent — the client's request timeout",
    title: "Rate-limiting data unavailable",
    detail: "No response within 30s.",
    retryable: true,
  },
  {
    id: "rejected",
    label: "Upstream rejected the query, and no Retry is offered",
    title: "Rate-limiting data unavailable",
    detail: 'Prometheus error (bad_data): invalid parameter "query": 1:1: parse error: unexpected identifier "sum_"',
    retryable: false,
  },
  {
    id: "long-body",
    label: "Long body — truncated, full text behind the disclosure",
    title: "Rate-limiting data unavailable",
    detail: LONG_BODY,
    retryable: true,
  },
  {
    id: "bare",
    label: "Neither title nor detail supplied — the bare fallback",
    retryable: false,
  },
];

/**
 * Visual reference for the states a chart shows when its data does not arrive.
 *
 * Deliberately not linked from the navigation — it is a developer aid, reachable
 * at /dev/chart-states. Keeping the states side by side makes it easy to check
 * that loading and failure share a footprint, so a chart flipping between them
 * does not shift the page.
 */
export default function ChartStatesPage() {
  return (
    <>
      <h1>Chart states</h1>
      <p>
        Every card below is a real ChartPlaceholder or ChartError. Retry swaps in the loading placeholder for a moment,
        so the two footprints can be compared directly.
      </p>

      <h2 className="section-header">Loading</h2>
      <div className="plots">
        <h3>ChartPlaceholder</h3>
        <ChartPlaceholder height={CHART_HEIGHT} />
      </div>

      <h2 className="section-header">Failure</h2>
      {SCENARIOS.map(scenario => (
        <div className="plots" key={scenario.id}>
          <h3>{scenario.label}</h3>
          <ScenarioCard scenario={scenario} />
        </div>
      ))}
    </>
  );
}

/** Flashes the loading placeholder on Retry, mirroring what a real chart does. */
function ScenarioCard({ scenario }: { scenario: Scenario }) {
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (!reloading) return;
    const timer = setTimeout(() => setReloading(false), 1200);
    return () => clearTimeout(timer);
  }, [reloading]);

  if (reloading) return <ChartPlaceholder height={CHART_HEIGHT} />;

  return (
    <ChartError
      title={scenario.title}
      detail={scenario.detail}
      onRetry={scenario.retryable ? () => setReloading(true) : undefined}
      height={CHART_HEIGHT}
    />
  );
}
